<template>
  <div>
    <b-table
      striped
      hover
      :fields="fields"
      :items="items"
      :busy="busy"
      :id="tabId"
      :per-page="pagination.perPage"
      :current-page="pagination.currentPage"
      :sort-compare="sortCompare"
      @sort-changed="onSort"
    >
      <template slot="show_details" slot-scope="row" :mq="viewPorts.mobile">
        <button @click="row.toggleDetails" class="toggle">
          <i v-if="row.detailsShowing" class="fas fa-minus"></i>
          <i v-else class="fas fa-plus"></i>
        </button>
      </template>

      <template slot="row-details" slot-scope="row" :mq="viewPorts.mobile">
        <div cols="12" class="detailCell-wrapper">
          <p>{{ $t("booking.array.ref") }} :</p>
          <p>{{ row.item.ref }}</p>
        </div>

        <div cols="12" class="detailCell-wrapper">
          <p>{{ $t("booking.array.type") }} :</p>
          <p>{{ row.item.type }}</p>
        </div>

        <div
          v-if="isViewPortMobileSmall || isViewPortMobileMedium"
          cols="12"
          class="detailCell-wrapper"
        >
          <p>{{ $t("booking.array.status") }} :</p>
          <p :class="row.item.status.className">
            {{ row.item.status.status }}
          </p>
        </div>

        <div v-if="isViewPortMobileSmall">
          <div v-if="row.item.orderNumber" cols="12" class="detailCell-wrapper">
            <p>{{ orderNumberLabel }}</p>
            {{ row.value }}
          </div>

          <div
            v-if="row.item.destination.hotel"
            cols="12"
            class="detailCell-wrapper"
          >
            <p>{{ $t("_hotel") }} :</p>
            {{ row.item.destination.hotel }}
          </div>

          <div
            v-else-if="row.item.destination.location"
            cols="12"
            class="detailCell-wrapper"
          >
            <p>{{ $t("_location") }} :</p>
            {{ row.item.destination.location }}
          </div>

          <div
            v-if="row.item.destination.city"
            cols="12"
            class="detailCell-wrapper"
          >
            <p
              v-if="row.item.destination.hotel || row.item.destination.location"
            >
              <!-- display city label only if it is definitely a city-->
              {{ $t("_city") }} :
            </p>

            <p v-else>{{ $t("_destination") }} :</p>
            {{ row.item.destination.city }}
          </div>
        </div>

        <div
          v-if="tabId !== 'history' && row.item.deadline"
          cols="12"
          class="detailCell-wrapper"
        >
          <p>{{ $t("booking.array.deadline") }} :</p>
          <p :class="row.item.deadline.class">
            {{ row.item.deadline.value }}
          </p>
        </div>
      </template>

      <template slot="dates" slot-scope="row">
        <div>
          <span>{{ $t("_period.from") }} :</span>
          {{ row.value.from }}
        </div>

        <div>
          <span>{{ $t("_period.to") }} :</span>
          {{ row.value.to }}
        </div>
      </template>

      <template
        slot="destination"
        slot-scope="row"
        :mq="
          viewPorts.mobileLarge.concat(
            viewPorts.mobileMedium,
            viewPorts.desktop
          )
        "
      >
        <div v-if="row.value.hotel">
          <span>{{ $t("_hotel") }} :</span>
          {{ truncate(row.value.hotel) }}
        </div>

        <div v-else-if="row.value.location">
          <span>{{ $t("_location") }} :</span>
          {{ truncate(row.value.location) }}
        </div>

        <div v-if="row.value.city">
          <span v-if="row.value.hotel || row.value.location">
            <!-- display label only if it is definitely a city-->
            {{ $t("_city") }} :
          </span>

          {{ truncate(row.value.city) }}
        </div>
      </template>

      <template slot="travellers" slot-scope="row">
        <span v-html="row.value"></span>
      </template>

      <template slot="actions" slot-scope="row">
        <p class="flex-option" v-if="tabId === 'history'">
          <list-btn :id="row.value.booking.id" default-state="giveOpinion" />

          <list-btn
            :id="row.value.booking.id"
            default-state="show"
            :history-table="true"
            class="btn-details"
          />
        </p>

        <div v-else class="actions-btn">
          <multiselect
            v-if="row.value.in.length"
            value="defaultSelected"
            :options="row.value.in"
            track-by="name"
            label="label"
            :placeholder="$t('booking.select.default')"
            select-label=""
            selected-label=""
            deselect-label=""
            :searchable="false"
            @select="selectedAction($event)"
          >
            <template slot="noOptions">
              {{ $t("_empty_list") }}
            </template>

            <template slot="option" slot-scope="action">
              <span class="option__desc">
                <div class="option__title">
                  <fa :fa-class="buttons[action.option.name].faClass" />
                  {{ action.option.label }}
                </div>
              </span>
            </template>
          </multiselect>

          <div class="approval-btn" :mq="viewPorts.desktop">
            <list-btn
              v-for="(val, key) in row.value.out"
              :default-state="key"
              :booking-status="row.item.actions.booking.status"
              :id="row.item.actions.booking.id"
              @click="selectedAction(val)"
            />
          </div>
        </div>
      </template>

      <template slot="deadline" slot-scope="row" :mq="viewPorts.desktop">
        <div :class="row.value.class">
          {{ row.value.value }}
        </div>
      </template>

      <template slot="type" slot-scope="row" :mq="viewPorts.desktop">
        <div>{{ row.value }}</div>
        <div>{{ row.item.ref }}</div>
      </template>

      <template
        slot="status"
        slot-scope="row"
        :mq="viewPorts.mobileLarge.concat(viewPorts.desktop)"
      >
        <span :class="row.value.className">
          {{ row.value.status }}
        </span>
      </template>

      <template
        slot="orderNumber"
        slot-scope="row"
        :mq="viewPorts.mobileLarge.concat(viewPorts.desktop)"
      >
        <span>{{ row.value }}</span>
      </template>
    </b-table>

    <component
      :is="modal.name"
      ref="modal"
      v-bind="modal.props"
      @ok="confirmAction"
      @submit="selectedAction($event)"
    />

    <div v-if="managePagination">
      <b-pagination
        v-if="displayPagination"
        v-model="pagination.currentPage"
        :total-rows="items.length"
        :per-page="pagination.perPage"
        :aria-controls="tabId"
      />

      <p v-else class="notfound">
        {{ notFoundMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import CommonApi from "@/api/commonApi";
import formatter from "@/components/global/mixins/formatterMethods";
import bookingDeadline from "@/components/frontoffice/mixins/bookingDeadline";
import BookingModalCancel from "@/components/frontoffice/Booking/modal/cancel";
import BookingModalConfirm from "@/components/frontoffice/Booking/modal/confirm";
import BookingModalAllow from "@/components/frontoffice/Booking/modal/allow";
import BookingModalDeny from "@/components/frontoffice/Booking/modal/deny";
import BookingModalOnlineEdit from "@/components/frontoffice/Booking/modal/onlineEdit";
import BookingModalSendVoucher from "@/components/frontoffice/Booking/modal/sendVoucher";
import BookingModalOrderNumber from "@/components/frontoffice/Booking/modal/orderNumber";
import listBtn from "@/components/frontoffice/button/list";
import viewPorts from "@/conf/viewPort";
import fa from "@/components/global/icon/fontAwesome";
import buttons from "@/components/frontoffice/mixins/buttons";
import { status } from "../../../utils/bookingStatus";

export default {
  components: {
    BookingModalAllow,
    BookingModalConfirm,
    BookingModalCancel,
    BookingModalDeny,
    BookingModalOnlineEdit,
    BookingModalSendVoucher,
    BookingModalOrderNumber,
    listBtn,
    fa,
  },

  mixins: [formatter, bookingDeadline, viewPorts, buttons],

  props: {
    tabId: String,
    busy: Boolean,
    fields: Array,
    items: Array,
    managePagination: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      commonApi: new CommonApi(),
      pagination: {
        perPage: 10,
        currentPage: 1,
      },
      modal: {
        name: null,
        props: {
          bookingId: null,
          isOnline: false,
          bookingStatus: null,
          deadline: null,
        },
      },
      currentBooking: null,
    };
  },

  methods: {
    sortCompare(
      aRow,
      bRow,
      key,
      sortDesc,
      formatter,
      compareOptions,
      compareLocale
    ) {
      if (this.tabId !== "history") {
        switch (key) {
          case "dates":
            let aFrom = this.stringToDate(aRow.dates.from);
            let bFrom = this.stringToDate(bRow.dates.from);

            let ret = aFrom < bFrom ? -1 : aFrom > bFrom ? 1 : 0;

            return aFrom < bFrom ? -1 : aFrom > bFrom ? 1 : 0;
          case "destination":
            return aRow.destination.city.localeCompare(bRow.destination.city);
          case "type":
            let aRef = aRow.ref || "";
            let bRef = bRow.ref || "";

            return aRef.localeCompare(bRef, compareLocale, compareOptions);
        }
      }
    },

    stringToDate(string) {
      let parts = string.split(" ");
      let day = parts[0];
      let month = parts[1];
      let year = parts[2];

      switch (month.toLowerCase()) {
        case "janvier":
        case "janv.":
          month = 0;
          break;
        case "février":
        case "févr.":
          month = 1;
          break;
        case "mars":
          month = 2;
          break;
        case "avril":
        case "avr.":
          month = 3;
          break;
        case "mai":
          month = 4;
          break;
        case "juin":
          month = 5;
          break;
        case "juillet":
        case "juil.":
          month = 6;
          break;
        case "août":
          month = 7;
          break;
        case "septembre":
        case "sept.":
          month = 8;
          break;
        case "octobre":
        case "oct.":
          month = 9;
          break;
        case "novembre":
        case "nov.":
          month = 10;
          break;
        case "décembre":
        case "déc.":
          month = 11;
          break;
      }

      return new Date(year, month, day);
    },

    selectedAction(event) {
      let action = event.name;
      this.currentBooking = event.booking;

      let redirect = (routeName, addParams = {}) => {
        let params = { id: this.currentBooking.id };

        this.$router.push({
          name: routeName,
          params: { ...params, ...addParams },
        });
      };

      let requireModal = false;
      let actionRef = action;

      switch (action) {
        case "duplicate":
        case "show":
          redirect("home_booking_" + action, {
            isOnline: this.currentBooking.is_online,
          });
          break;
        case "edit":
        case "confirm_edit":
          if (this.currentBooking.status === status.UNFINISHED_OFFLINE_BOOKING || this.currentBooking.status === status.DRAFT_OFFLINE_BOOKING) {
            action = "offline-edit";
            redirect("offline_booking_edit", { isDraft: this.currentBooking.status === status.DRAFT_OFFLINE_BOOKING });
          } else if (this.currentBooking.is_online && action == "edit") {
            action = "online-edit";
            actionRef = "online_edit";
            requireModal = true;
          } else {
            redirect("home_booking_edit");
          }
          break;
        case "contactBBA":
          redirect("home_message_create");
          break;
        case "allow":
        case "confirm":
        case "deny":
          requireModal = true;
          break;
        case "order_number":
          action = "order-number";
          requireModal = true;
          break;
        case "online_validate":
          if (this.currentBooking.is_online) {
            actionRef = action;
            action = "confirm";
          }
          requireModal = true;
          break;
        case "send_voucher":
          action = "send-voucher";
          requireModal = true;
          break;
        case "cancel":
          // in case of timeout deadline, redirect to contact BBA
          if (
            this.cancellationThroughNewWindow(this.currentBooking) &&
            !this.currentBooking.is_online
          )
            redirect("home_message_create", { action: "cancel" });

          requireModal = true;
          break;
        case "download_ics":
        case "download_pdf":
          window.open(
            this.commonApi.downloadBookingUrl(
              this.currentBooking.id,
              action.substring(action.indexOf("_") + 1)
            ),
            "_blank"
          );
          break;
        case "download_order_pdf":
          window.open(
            this.commonApi.downloadOrderPdfUrl(this.currentBooking.id),
            "_blank"
          );
          break;
      }

      if (requireModal) {
        this.modal = {
          name: `booking-modal-${action}`,
          props: {
            booking: this.currentBooking,
            bookingId: this.currentBooking.id,
            isOnline: this.currentBooking.is_online,
            bookingStatus: this.currentBooking.status,
            deadline: this.currentBooking.cancellation_deadline,
          },
        };

        this.$nextTick(() => {
          this.$refs.modal.$refs[actionRef].show();
        });
      }
    },

    confirmAction(data) {
      if (data.success) {
        this.$emit("confirmAction", data.message);
      }
    },

    onSort(options) {
      this.$emit("sortChanged", options);
    },

    truncate(val) {
      let length = this.isViewPortMobileMedium
        ? 10
        : this.isViewPortMobileLarge
        ? 15
        : 20;

      return this.$options.filters.truncate(val, length);
    },
  },

  computed: {
    notFoundMessage() {
      return this.$t("booking.notFoundMessage." + this.tabId);
    },

    displayPagination() {
      return this.managePagination && this.items.length > 0;
    },

    orderNumber() {
      let orderNumber = this.$store.getters["security/company"].orderNumber;

      return orderNumber !== null && orderNumber.required ? orderNumber : null;
    },

    orderNumberLabel() {
      return this.orderNumber ? this.orderNumber.label : null;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/sass/common/color.scss";

.confirmed {
  color: green;
}

.transitional {
  color: $orange;
}

.cancelled {
  color: red;
}
</style>
